export interface EsPdfToolMeta {
  slug: string;
  enSlug: string;
  name: string;
  tagline: string;
  description: string;
  category: "Organizar y Editar" | "Optimizar y Reparar" | "Convertir a PDF" | "Convertir desde PDF" | "Seguridad y Utilidades";
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const ES_PDF_CATEGORIES = [
  "Organizar y Editar",
  "Optimizar y Reparar",
  "Convertir a PDF",
  "Convertir desde PDF",
  "Seguridad y Utilidades",
] as const;

export type EsPdfCategory = (typeof ES_PDF_CATEGORIES)[number];

export const ES_PDF_TOOLS: EsPdfToolMeta[] = [
  // Organizar y Editar
  {
    slug: "unir-pdf",
    enSlug: "merge-pdf",
    name: "Unir PDF Gratis Online",
    tagline: "Combina varios archivos PDF en un único documento limpio",
    description: "Junta dos o más documentos PDF en un solo archivo ordenado. Elige el orden de las páginas y descarga en segundos con total privacidad.",
    category: "Organizar y Editar",
    icon: "FilePlus",
    featured: true,
    keywords: ["unir pdf", "combinar pdf", "juntar pdf", "unir archivos pdf gratis"],
  },
  {
    slug: "dividir-pdf",
    enSlug: "split-pdf",
    name: "Dividir PDF Online",
    tagline: "Extrae páginas o separa tu documento en archivos independientes",
    description: "Separa páginas específicas o extrae rangos personalizados de tu archivo PDF fácilmente.",
    category: "Organizar y Editar",
    icon: "Scissors",
    featured: true,
    keywords: ["dividir pdf", "separar paginas pdf", "extraer pdf gratis"],
  },
  {
    slug: "organizar-pdf",
    enSlug: "organize-pdf",
    name: "Organizar Páginas de PDF",
    tagline: "Reordena, elimina y gira páginas de forma visual e intuitiva",
    description: "Arrastra y suelta páginas para cambiar su orden, borra las hojas innecesarias o rotralas individualmente.",
    category: "Organizar y Editar",
    icon: "LayoutGrid",
    featured: true,
    keywords: ["organizar pdf", "reordenar paginas pdf", "borrar paginas pdf"],
  },
  {
    slug: "rotar-pdf",
    enSlug: "rotate-pdf",
    name: "Rotar PDF Online",
    tagline: "Gira todas o algunas páginas de tu PDF permanentemente",
    description: "Corrige páginas invertidas o de lado girando hojas específicas a 90, 180 o 270 grados.",
    category: "Organizar y Editar",
    icon: "RotateCw",
    keywords: ["rotar pdf", "girar pdf", "cambiar orientacion pdf"],
  },
  {
    slug: "extraer-paginas-pdf",
    enSlug: "extract-pages",
    name: "Extraer Páginas de PDF",
    tagline: "Guarda páginas seleccionadas en un nuevo documento PDF",
    description: "Selecciona las hojas que necesitas de un PDF extenso y expórtalas en un archivo totalmente nuevo.",
    category: "Organizar y Editar",
    icon: "FileOutput",
    keywords: ["extraer paginas pdf", "guardar paginas especificas pdf"],
  },
  {
    slug: "eliminar-paginas-pdf",
    enSlug: "remove-pages",
    name: "Eliminar Páginas de PDF",
    tagline: "Quita hojas en blanco o innecesarias de tu documento PDF",
    description: "Elimina de forma permanente páginas no deseadas de cualquier documento PDF en tu navegador.",
    category: "Organizar y Editar",
    icon: "Trash2",
    keywords: ["eliminar paginas pdf", "quitar hoja pdf", "borrar pagina pdf"],
  },
  {
    slug: "editar-pdf",
    enSlug: "edit-pdf",
    name: "Editor de PDF Online Gratis",
    tagline: "Añade texto, firmas, formas, resaltados e imágenes",
    description: "Agrega anotaciones, cuadros de texto, dibujos a mano alzada, firmas e imágenes directamente sobre las páginas de tu PDF.",
    category: "Organizar y Editar",
    icon: "Edit3",
    featured: true,
    keywords: ["editar pdf", "añadir texto a pdf", "escribir en pdf gratis"],
  },
  {
    slug: "numerar-paginas-pdf",
    enSlug: "page-numbers",
    name: "Numerar Páginas de PDF",
    tagline: "Añade números de página personalizados en encabezado o pie",
    description: "Pon número a las hojas de tu PDF personalizando posición, tamaño de letra, formato y márgenes.",
    category: "Organizar y Editar",
    icon: "Hash",
    keywords: ["numerar paginas pdf", "poner numeros a pdf", "insertar numero de pagina"],
  },
  {
    slug: "marca-de-agua-pdf",
    enSlug: "watermark-pdf",
    name: "Marca de Agua en PDF",
    tagline: "Inserta marcas de agua de texto o logotipo en tus documentos",
    description: "Protege tu propiedad intelectual stampando texto o logos transparentes sobre las páginas de tu archivo PDF.",
    category: "Organizar y Editar",
    icon: "Droplets",
    keywords: ["marca de agua pdf", "poner marca de agua pdf", "marca de agua texto pdf"],
  },

  // Optimizar y Reparar
  {
    slug: "comprimir-pdf",
    enSlug: "compress-pdf",
    name: "Comprimir PDF Gratis",
    tagline: "Reduce el tamaño de tu archivo PDF sin perder calidad",
    description: "Comprime documentos PDF pesados para enviarlos por correo electrónico o subirlos a la web conservando su nitidez.",
    category: "Optimizar y Reparar",
    icon: "Archive",
    featured: true,
    keywords: ["comprimir pdf", "reducir tamaño pdf", "bajar peso pdf"],
  },
  {
    slug: "convertir-pdf-blanco-y-negro",
    enSlug: "grayscale-pdf",
    name: "PDF a Escala de Grises",
    tagline: "Convierte las páginas de tu PDF a blanco y negro",
    description: "Transforma documentos PDF a color en escala de grises para ahorrar tinta y tóner al imprimir.",
    category: "Optimizar y Reparar",
    icon: "SunMedium",
    keywords: ["convertir pdf a blanco y negro", "pdf escala de grises"],
  },
  {
    slug: "reparar-pdf",
    enSlug: "repair-pdf",
    name: "Reparar PDF Dañado",
    tagline: "Recupera y arregla archivos PDF ilegibles o corruptos",
    description: "Intenta recuperar la información y reparar documentos PDF dañados directamente en tu navegador.",
    category: "Optimizar y Reparar",
    icon: "Wrench",
    keywords: ["reparar pdf", "arreglar pdf dañado", "recuperar archivo pdf"],
  },

  // Convertir a PDF
  {
    slug: "convertir-imagen-a-pdf",
    enSlug: "image-to-pdf",
    name: "Convertir Imagen a PDF (JPG a PDF)",
    tagline: "Convierte imágenes JPG, PNG, WEBP y GIF a formato PDF",
    description: "Junta varias fotos e imágenes en un solo documento PDF perfectamente maquetado.",
    category: "Convertir a PDF",
    icon: "Image",
    featured: true,
    keywords: ["convertir imagen a pdf", "jpg a pdf", "png a pdf gratis"],
  },
  {
    slug: "convertir-word-a-pdf",
    enSlug: "word-to-pdf",
    name: "Convertir Word a PDF",
    tagline: "Convierte archivos DOCX y DOC a PDF profesional",
    description: "Transforma tus documentos de Word a formato PDF respetando el diseño y el texto.",
    category: "Convertir a PDF",
    icon: "FileText",
    keywords: ["word a pdf", "docx a pdf gratis", "convertir word a pdf"],
  },
  {
    slug: "convertir-excel-a-pdf",
    enSlug: "excel-to-pdf",
    name: "Convertir Excel a PDF",
    tagline: "Convierte hojas de cálculo XLSX a tablas PDF",
    description: "Pasa tus hojas de Excel a formato PDF listo para imprimir o presentar.",
    category: "Convertir a PDF",
    icon: "Table",
    keywords: ["excel a pdf", "xlsx a pdf gratis"],
  },
  {
    slug: "convertir-powerpoint-a-pdf",
    enSlug: "ppt-to-pdf",
    name: "Convertir PowerPoint a PDF",
    tagline: "Convierte diapositivas PPTX a documento PDF",
    description: "Transforma tus presentaciones de PowerPoint en documentos PDF compactos.",
    category: "Convertir a PDF",
    icon: "Presentation",
    keywords: ["powerpoint a pdf", "pptx a pdf gratis"],
  },
  {
    slug: "convertir-html-a-pdf",
    enSlug: "html-to-pdf",
    name: "Convertir HTML a PDF",
    tagline: "Guarda páginas web o código HTML en PDF",
    description: "Convierte código HTML o contenido web en un archivo PDF descargable.",
    category: "Convertir a PDF",
    icon: "Code",
    keywords: ["html a pdf", "pagina web a pdf"],
  },
  {
    slug: "convertir-texto-a-pdf",
    enSlug: "txt-to-pdf",
    name: "Convertir Texto (TXT) a PDF",
    tagline: "Convierte archivos de texto plano a documentos PDF",
    description: "Pasa archivos de notas .txt a formato PDF con estilo de fuente personalizado.",
    category: "Convertir a PDF",
    icon: "FileType",
    keywords: ["txt a pdf", "texto a pdf gratis"],
  },

  // Convertir desde PDF
  {
    slug: "convertir-pdf-a-imagen",
    enSlug: "pdf-to-image",
    name: "Convertir PDF a Imagen (JPG / PNG)",
    tagline: "Extrae páginas de tu PDF como imágenes en alta resolución",
    description: "Guarda cada página de tu archivo PDF en fotos JPG o PNG de alta calidad.",
    category: "Convertir desde PDF",
    icon: "FileImage",
    featured: true,
    keywords: ["pdf a imagen", "pdf a jpg gratis", "pdf a png"],
  },
  {
    slug: "convertir-pdf-a-word",
    enSlug: "pdf-to-word",
    name: "Convertir PDF a Word",
    tagline: "Extrae el texto y convierte PDF a DOCX editable",
    description: "Convierte documentos PDF en archivos editable de Word conservando la disposición del texto.",
    category: "Convertir desde PDF",
    icon: "FileCode",
    keywords: ["pdf a word", "pdf a docx editable"],
  },
  {
    slug: "convertir-pdf-a-excel",
    enSlug: "pdf-to-excel",
    name: "Convertir PDF a Excel",
    tagline: "Extrae tablas a hojas de cálculo XLSX",
    description: "Exporta tablas de datos de un PDF a hojas editables de Excel.",
    category: "Convertir desde PDF",
    icon: "Spreadsheet",
    keywords: ["pdf a excel", "pdf a xlsx gratis"],
  },
  {
    slug: "convertir-pdf-a-texto",
    enSlug: "pdf-to-text",
    name: "Extraer Texto de PDF",
    tagline: "Extrae todo el texto plano de un documento PDF",
    description: "Obtén el contenido escrito de un PDF en un práctico archivo de texto plano .txt.",
    category: "Convertir desde PDF",
    icon: "FileLines",
    keywords: ["extraer texto pdf", "pdf a txt"],
  },

  // Seguridad y Utilidades
  {
    slug: "proteger-pdf",
    enSlug: "protect-pdf",
    name: "Proteger PDF con Contraseña",
    tagline: "Encripta y bloquea tu PDF con clave de seguridad",
    description: "Protege documentos PDF confidenciales mediante encriptación y contraseña de apertura.",
    category: "Seguridad y Utilidades",
    icon: "Lock",
    featured: true,
    keywords: ["proteger pdf", "poner contraseña a pdf", "encriptar pdf"],
  },
  {
    slug: "desbloquear-pdf",
    enSlug: "unlock-pdf",
    name: "Desbloquear PDF",
    tagline: "Quita la contraseña y restricciones de tu PDF",
    description: "Elimina protecciones con contraseña y restricciones de edición de archivos PDF de tu propiedad.",
    category: "Seguridad y Utilidades",
    icon: "Unlock",
    keywords: ["desbloquear pdf", "quitar contraseña pdf", "remover clave pdf"],
  },
  {
    slug: "firmar-pdf",
    enSlug: "sign-pdf",
    name: "Firmar PDF Online",
    tagline: "Dibuja o sube tu firma digital en documentos PDF",
    description: "Dibuja tu firma a mano o sube una imagen de firma para rubricar contratos y formularios.",
    category: "Seguridad y Utilidades",
    icon: "PenTool",
    featured: true,
    keywords: ["firmar pdf", "firma digital pdf", "firmar contrato pdf online"],
  },
  {
    slug: "ocr-pdf",
    enSlug: "ocr-pdf",
    name: "Reconocimiento OCR en PDF",
    tagline: "Reconoce el texto de documentos PDF escaneados",
    description: "Utiliza reconocimiento óptico de caracteres para hacer buscables documentos PDF escaneados.",
    category: "Seguridad y Utilidades",
    icon: "ScanText",
    keywords: ["ocr pdf", "pdf escaneado a texto", "reconocimiento de texto pdf"],
  },
  {
    slug: "generador-codigo-qr",
    enSlug: "qr-code",
    name: "Generador de Código QR",
    tagline: "Crea códigos QR personalizados para enlaces, WiFi, vCards y texto",
    description: "Genera códigos QR diseñados para páginas web, redes WiFi, tarjetas de contacto y texto. Descárgalos en PNG o estámpalos en tus archivos PDF.",
    category: "Seguridad y Utilidades",
    icon: "QrCode",
    featured: true,
    keywords: ["generador codigo qr", "crear codigo qr gratis", "codigo qr para wifi", "codigo qr url"],
  },
];

export function getEsPdfToolBySlug(slug: string): EsPdfToolMeta | undefined {
  return ES_PDF_TOOLS.find((t) => t.slug === slug || t.enSlug === slug);
}

export function getEnPdfSlugForEsSlug(esSlug: string): string {
  const found = ES_PDF_TOOLS.find((t) => t.slug === esSlug || t.enSlug === esSlug);
  return found ? found.enSlug : esSlug;
}

export function getEsPdfSlugForEnSlug(enSlug: string): string {
  const found = ES_PDF_TOOLS.find((t) => t.enSlug === enSlug || t.slug === enSlug);
  return found ? found.slug : enSlug;
}

export function relatedEsPdfTools(currentSlug: string, count = 4): EsPdfToolMeta[] {
  const current = getEsPdfToolBySlug(currentSlug);
  return ES_PDF_TOOLS.filter((t) => t.slug !== current?.slug).slice(0, count);
}
